import { useState } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { MdCheckCircle, MdRocketLaunch, MdHelpOutline, MdExpandMore, MdExpandLess } from 'react-icons/md';
import { useAdvisor } from '../context/AdvisorContext';

function FlaskPlaceholder() {
  return (
    <div className="placeholder">
      <svg viewBox="0 0 200 240" width="148" height="178" fill="none">
        <defs>
          <linearGradient id="liqTop" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#818cf8" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#4338ca" stopOpacity="0.85" />
          </linearGradient>
          <linearGradient id="glowGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6366f1" stopOpacity="0" />
            <stop offset="100%" stopColor="#6366f1" stopOpacity="0.12" />
          </linearGradient>
          <clipPath id="flaskBody">
            <path d="M 80 28 L 80 98 L 22 192 Q 20 206 34 208 L 166 208 Q 180 206 178 192 L 120 98 L 120 28 Z" />
          </clipPath>
        </defs>
        <ellipse cx="100" cy="190" rx="70" ry="18" fill="#6366f1" opacity="0.08" />
        <path d="M 80 28 L 80 98 L 22 192 Q 20 206 34 208 L 166 208 Q 180 206 178 192 L 120 98 L 120 28 Z"
          fill="url(#glowGrad)" stroke="#334155" strokeWidth="2.5" strokeLinejoin="round" />
        <rect x="0" y="148" width="200" height="70" fill="url(#liqTop)" clipPath="url(#flaskBody)" />
        <path d="M 28 148 Q 55 142 82 148 Q 118 154 148 148 Q 163 145 172 148"
          stroke="#a5b4fc" strokeWidth="1.5" fill="none" opacity="0.7" clipPath="url(#flaskBody)" />
        {[{cx:78,r:4.5,dur:'2.2s',begin:'0s'},{cx:112,r:3,dur:'2.8s',begin:'0.9s'},{cx:94,r:2,dur:'1.9s',begin:'1.7s'},{cx:130,r:3.5,dur:'2.5s',begin:'0.4s'}].map(b => (
          <circle key={b.cx} cx={b.cx} cy="170" r={b.r} fill="#c7d2fe" opacity="0">
            <animate attributeName="cy" from="196" to="152" dur={b.dur} begin={b.begin} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0;0.5;0" dur={b.dur} begin={b.begin} repeatCount="indefinite" />
          </circle>
        ))}
        <line x1="74" y1="28" x2="126" y2="28" stroke="#475569" strokeWidth="2.5" strokeLinecap="round" />
        <path d="M 85 42 L 85 92 L 32 178" stroke="#e2e8f0" strokeWidth="1.8" strokeLinecap="round" opacity="0.12" />
      </svg>
      <p className="placeholder__title">Your strategy workspace</p>
      <p className="placeholder__body">
        Chat with the advisor to generate your personalised strategy parameters,
        backtest performance, and portfolio holdings.
      </p>
    </div>
  );
}

// ── Profile dial + tilt sliders (primary Strategy controls) ─────────────

function ProfileDial({ profileKeys, profiles, activeKey, onSelect }) {
  return (
    <div className="profile-dial">
      {profileKeys.map(key => (
        <button
          key={key}
          type="button"
          className={`profile-btn ${activeKey === key ? 'profile-btn--active' : ''}`}
          onClick={() => onSelect(key)}
        >
          {profiles[key].name}
        </button>
      ))}
      {!activeKey && <span className="profile-custom-badge">Custom</span>}
    </div>
  );
}

function TiltSlider({ label, helper, value, onChange, leftLabel, rightLabel, disabled }) {
  // Snap to 0 within ±0.5 to give the slider a "neutral" detent.
  const display = Math.abs(value) < 0.5 ? 0 : Math.round(value);
  const sign = display > 0 ? '+' : '';
  return (
    <div className={`tilt-slider ${disabled ? 'tilt-slider--disabled' : ''}`}>
      <div className="tilt-slider__head">
        <span className="tilt-slider__label">{label}</span>
        <span className={`tilt-slider__value ${display > 0 ? 'tilt-slider__value--pos' : display < 0 ? 'tilt-slider__value--neg' : ''}`}>
          {display === 0 ? 'Baseline' : `${sign}${display}pp`}
        </span>
      </div>
      <input
        type="range"
        min={-10}
        max={10}
        step={1}
        value={display}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        disabled={disabled}
        aria-label={label}
      />
      <div className="tilt-slider__ends">
        <span>{leftLabel}</span>
        <span>{rightLabel}</span>
      </div>
      {helper && <p className="tilt-slider__helper">{helper}</p>}
    </div>
  );
}

function ParamCard({ label, value, unit, color, max = 100, description }) {
  return (
    <div className="param-card">
      {description && (
        <span className="stat-card__help" data-tip={description} aria-label={description}>
          <MdHelpOutline size={13} />
        </span>
      )}
      <div className="param-card__value" style={{ color }}>{value}<span className="param-card__unit">{unit}</span></div>
      <div className="param-card__label">{label}</div>
      <div className="param-card__bar">
        <div className="param-card__bar-fill" style={{ width: `${(value / max) * 100}%`, background: color }} />
      </div>
    </div>
  );
}

const PARAM_META = [
  {
    key: 'grossExposure',
    label: 'Total Invested',
    unit: '%',
    color: '#00ff88',
    max: 200,
    description: '100% means fully invested with the money you put in. Above 100% means we use a small amount of borrowing to put more to work — e.g. 140% = $1.40 invested for every $1 of yours.',
  },
  {
    key: 'shortPercent',
    label: 'Drop Hedge',
    unit: '%',
    color: '#ff5b5b',
    max: 100,
    description: 'Short positions that profit when specific stocks fall — they cushion losses during market drops.',
  },
  {
    key: 'goldPercent',
    label: 'Gold',
    unit: '%',
    color: '#eab308',
    max: 100,
    description: 'Held as protection against inflation and as a safe haven during market turmoil.',
  },
  {
    key: 'bondsPercent',
    label: 'Bonds',
    unit: '%',
    color: '#f59e0b',
    max: 100,
    description: 'High-quality bonds for steady income and stability.',
  },
];

// ── Outcome cards (retail-friendly replacements for the stats grid) ─────────

function OutcomeCard({ title, headline, headlineColor, subtext, description }) {
  return (
    <div className="outcome-card">
      {description && (
        <span className="stat-card__help" data-tip={description} aria-label={description}>
          <MdHelpOutline size={13} />
        </span>
      )}
      <div className="outcome-card__title">{title}</div>
      <div className="outcome-card__headline" style={headlineColor ? { color: headlineColor } : undefined}>
        {headline}
      </div>
      {subtext && <div className="outcome-card__subtext">{subtext}</div>}
    </div>
  );
}

// Annualized standard deviation of S&P 500 monthly returns from backtest data.
function computeBenchAnnualStdDev(rows) {
  if (!Array.isArray(rows) || rows.length < 2) return null;
  const series = rows.map(r => r['S&P 500']).filter(Number.isFinite);
  if (series.length < 2) return null;
  const ret = [];
  for (let i = 1; i < series.length; i++) {
    if (series[i - 1] > 0) ret.push((series[i] - series[i - 1]) / series[i - 1]);
  }
  if (!ret.length) return null;
  const mean = ret.reduce((a, b) => a + b, 0) / ret.length;
  const variance = ret.reduce((a, b) => a + (b - mean) ** 2, 0) / ret.length;
  return Math.sqrt(variance) * Math.sqrt(12) * 100;  // annualized %
}

// Largest peak-to-trough drawdown of the strategy series with peak/trough/recovery dates.
function findMaxDrawdownInfo(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  let peak = rows[0].Strategy;
  let peakDate = rows[0].date;
  let maxDD = 0;
  let maxPeak = peak, maxPeakDate = peakDate, maxTroughDate = peakDate;
  for (const r of rows) {
    if (r.Strategy > peak) { peak = r.Strategy; peakDate = r.date; }
    const dd = peak > 0 ? (r.Strategy - peak) / peak : 0;
    if (dd < maxDD) {
      maxDD = dd;
      maxPeak = peak;
      maxPeakDate = peakDate;
      maxTroughDate = r.date;
    }
  }
  let recoveryDate = null;
  let pastTrough = false;
  for (const r of rows) {
    if (r.date === maxTroughDate) pastTrough = true;
    if (pastTrough && r.Strategy >= maxPeak) { recoveryDate = r.date; break; }
  }
  return {
    pct: maxDD * 100,
    peakValue: maxPeak,
    peakDate: maxPeakDate,
    troughDate: maxTroughDate,
    recoveryDate,
  };
}

function fmtMonthYear(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleString('en-US', { month: 'short', year: 'numeric' });
}

function monthsBetween(d1, d2) {
  if (!d1 || !d2) return null;
  const a = new Date(d1), b = new Date(d2);
  if (isNaN(a.getTime()) || isNaN(b.getTime())) return null;
  return (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
}

function AllocationBar({ params }) {
  const l = 100 - params.shortPercent - params.goldPercent - params.bondsPercent;
  const segs = [
    { label: `Stocks (long) ${l}%`,            pct: l,                    color: '#00ff88' },
    { label: `Drop hedge ${params.shortPercent}%`, pct: params.shortPercent, color: '#ff5b5b' },
    { label: `Gold ${params.goldPercent}%`,        pct: params.goldPercent,  color: '#eab308' },
    { label: `Bonds ${params.bondsPercent}%`,      pct: params.bondsPercent, color: '#f59e0b' },
  ].filter(s => s.pct > 0);

  return (
    <div className="alloc-bar-wrap">
      <div className="alloc-bar">
        {segs.map(s => <div key={s.label} className="alloc-bar__seg" style={{ width: `${s.pct}%`, background: s.color }} title={s.label} />)}
      </div>
      <div className="alloc-legend">
        {segs.map(s => (
          <span key={s.label} className="alloc-legend__item">
            <span className="alloc-legend__dot" style={{ background: s.color }} />{s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

const tooltipStyle = { backgroundColor: '#1c2230', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#e6e8eb', fontSize: 12 };
// Direction A (Robinhood-leaning) chart palette: electric mint for the
// strategy, neutral grey for the benchmark, dark grid lines so they
// recede on the near-black canvas.
const CHART_STRATEGY = '#00ff88';
const CHART_BENCH    = '#5a6068';
const CHART_GRID     = 'rgba(255,255,255,0.06)';
const CHART_AXIS     = '#5a6068';
const fmtDollar = v => `$${Number(v).toLocaleString()}`;

export default function WorkingArea() {
  const {
    phase, params, backtestData, stats, deploy, resetConversation, allocationUsd,
    riskProfiles, riskProfileKeys, activeProfile, profileTilts,
    selectProfile, setTilt,
  } = useAdvisor();
  const [showTechnical, setShowTechnical] = useState(false);

  if (!params || !backtestData) return <FlaskPlaceholder />;

  // Scale the $10k-baseline backtest to the user's actual allocation (set by
  // the agent at deploy time). Pre-deploy, default to $10k for the "what-if".
  const baseAlloc = (allocationUsd && allocationUsd > 0) ? allocationUsd : 10000;
  const scale     = baseAlloc / 10000;
  const chartData = scale === 1
    ? backtestData
    : backtestData.map(r => ({ date: r.date, Strategy: r.Strategy * scale, 'S&P 500': r['S&P 500'] * scale }));

  const last        = chartData[chartData.length - 1];
  const stratReturn = (((last.Strategy   / baseAlloc) - 1) * 100).toFixed(1);
  const benchReturn = (((last['S&P 500'] / baseAlloc) - 1) * 100).toFixed(1);

  const yearsSeen = new Set();
  const xTickFmt = (dateStr) => {
    const y = new Date(dateStr).getFullYear();
    if (yearsSeen.has(y)) return '';
    yearsSeen.add(y);
    return String(y);
  };

  // Outcome-card derivations (only when stats are available).
  const ddInfo   = stats ? findMaxDrawdownInfo(backtestData) : null;
  const benchVol = stats ? computeBenchAnnualStdDev(backtestData) : null;
  // For the "Steadiness" verdict we compare the strategy's own volatility to
  // the S&P 500's. Strategy stats use stdDev as an annualized % already.
  const stratVol = stats?.stdDev ?? null;
  const ratio    = (stratVol != null && benchVol != null && benchVol > 0) ? stratVol / benchVol : null;
  const steadiness =
    ratio == null      ? { word: '—',                color: '#64748b' }
    : ratio < 0.9      ? { word: 'Smoother',         color: '#00ff88' }
    : ratio <= 1.1     ? { word: 'About the same as',color: '#ffb020' }
    :                    { word: 'Bumpier',          color: '#ff5b5b' };

  // Worst-stretch dollar loss + recovery duration in plain English.
  const ddDollarLoss = (ddInfo && ddInfo.pct < 0)
    ? Math.abs(ddInfo.pct) / 100 * baseAlloc
    : null;
  const ddDurationMonths = ddInfo ? monthsBetween(ddInfo.peakDate, ddInfo.recoveryDate) : null;

  return (
    <div className="results-scroll">

      <div className="top-row">
        <section className="results-section top-row__col">
          <h2 className="results-title">Your Strategy Mix</h2>
          <p className="results-subtitle">
            {activeProfile ? (
              <>
                Profile: <strong style={{ color: '#00ff88' }}>{riskProfiles[activeProfile].name}</strong>
                &nbsp;·&nbsp; stocks (long) <strong>{100 - params.shortPercent - params.goldPercent - params.bondsPercent}%</strong>
              </>
            ) : (
              <>
                Custom mix · total invested <strong style={{ color: '#00ff88' }}>{params.grossExposure}%</strong>
                &nbsp;·&nbsp; stocks (long) <strong>{100 - params.shortPercent - params.goldPercent - params.bondsPercent}%</strong>
              </>
            )}
          </p>

          <ProfileDial
            profileKeys={riskProfileKeys}
            profiles={riskProfiles}
            activeKey={activeProfile}
            onSelect={selectProfile}
          />

          {activeProfile ? (
            <div className="tilt-stack">
              <TiltSlider
                label="Drop hedge"
                value={profileTilts.shortPercent}
                onChange={(v) => setTilt('shortPercent', v)}
                leftLabel="Less hedge"
                rightLabel="More hedge"
                helper={`Baseline ${riskProfiles[activeProfile].shortPercent}%`}
              />
              <TiltSlider
                label="Gold"
                value={profileTilts.goldPercent}
                onChange={(v) => setTilt('goldPercent', v)}
                leftLabel="Less gold"
                rightLabel="More gold"
                helper={`Baseline ${riskProfiles[activeProfile].goldPercent}%`}
              />
              <TiltSlider
                label="Bonds"
                value={profileTilts.bondsPercent}
                onChange={(v) => setTilt('bondsPercent', v)}
                leftLabel="Less bonds"
                rightLabel="More bonds"
                helper={`Baseline ${riskProfiles[activeProfile].bondsPercent}%`}
              />
            </div>
          ) : (
            <p className="custom-hint">
              Pick a profile above to enable nudges, or keep this custom mix that the advisor set for you.
            </p>
          )}

          <AllocationBar params={params} />

          <button
            type="button"
            className="tech-toggle"
            onClick={() => setShowTechnical(s => !s)}
            aria-expanded={showTechnical}
          >
            {showTechnical ? <MdExpandLess size={16} /> : <MdExpandMore size={16} />}
            {showTechnical ? 'Hide technical details' : 'Show technical details'}
          </button>

          {showTechnical && (
            <div className="params-grid">
              {PARAM_META.map(m => (
                <ParamCard
                  key={m.key}
                  label={m.label}
                  value={params[m.key]}
                  unit={m.unit}
                  color={m.color}
                  max={m.max}
                  description={m.description}
                />
              ))}
            </div>
          )}
        </section>

        {stats && (
          <section className="results-section top-row__col">
            <h2 className="results-title">What this could mean for you</h2>
            <p className="results-subtitle">
              Based on a 16-year historical backtest. Past performance doesn't guarantee future results.
            </p>
            <div className="outcome-stack">
              <OutcomeCard
                title="Growth potential"
                headline={
                  <>
                    {fmtDollar(Math.round(baseAlloc))} → <span style={{ color: stratReturn >= 0 ? '#00ff88' : '#ff5b5b' }}>{fmtDollar(Math.round(last.Strategy))}</span>
                  </>
                }
                subtext={`Over the last 16 years. The S&P 500 alone would have grown the same starting amount to ${fmtDollar(Math.round(last['S&P 500']))}.`}
                description="What your initial investment would have grown to if you'd run this strategy from January 2010 to today."
              />
              <OutcomeCard
                title="Worst stretch"
                headline={
                  ddDollarLoss != null
                    ? <span style={{ color: '#ff5b5b' }}>Lost {fmtDollar(Math.round(ddDollarLoss))} temporarily</span>
                    : '—'
                }
                subtext={
                  ddInfo
                    ? `From ${fmtMonthYear(ddInfo.peakDate)} to ${fmtMonthYear(ddInfo.troughDate)}` +
                      (ddInfo.recoveryDate
                        ? `, fully recovered by ${fmtMonthYear(ddInfo.recoveryDate)}` +
                          (ddDurationMonths ? ` (${ddDurationMonths} months total).` : '.')
                        : ' — had not yet fully recovered as of the backtest end.')
                    : null
                }
                description="The biggest peak-to-trough loss the strategy went through. You wouldn't have realized this loss unless you sold at the bottom — the portfolio recovered afterwards."
              />
              <OutcomeCard
                title="Steadiness"
                headline={
                  <span style={{ color: steadiness.color }}>
                    {steadiness.word}{steadiness.word === '—' ? '' : ' the S&P 500'}
                  </span>
                }
                subtext={
                  (stratVol != null && benchVol != null)
                    ? `Year-to-year, your portfolio's value swings about ${stratVol.toFixed(1)}% vs the S&P 500's ${benchVol.toFixed(1)}%.`
                    : null
                }
                description="How much your portfolio's value bounces around year-to-year. Smoother = fewer surprises in either direction."
              />
            </div>
          </section>
        )}
      </div>

      <section className="results-section">
        <h2 className="results-title">How {fmtDollar(baseAlloc)} would have grown</h2>
        <p className="results-subtitle">
          Strategy → <strong style={{ color: stratReturn >= 0 ? '#00ff88' : '#ff5b5b' }}>{fmtDollar(Math.round(last.Strategy))} ({stratReturn >= 0 ? '+' : ''}{stratReturn}%)</strong>
          &nbsp;·&nbsp;
          S&P 500 → <strong>{fmtDollar(Math.round(last['S&P 500']))} ({benchReturn >= 0 ? '+' : ''}{benchReturn}%)</strong>
        </p>
        <div className="chart-wrap">
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={chartData} margin={{ top: 5, right: 16, left: 10, bottom: 0 }}>
              <defs>
                <linearGradient id="stratGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={CHART_STRATEGY} stopOpacity={0.28} />
                  <stop offset="95%" stopColor={CHART_STRATEGY} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="benchGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={CHART_BENCH} stopOpacity={0.12} />
                  <stop offset="95%" stopColor={CHART_BENCH} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} />
              <XAxis dataKey="date" tickFormatter={xTickFmt} tick={{ fontSize: 11, fill: CHART_AXIS }} tickLine={false} interval={11} />
              <YAxis tickFormatter={v => `$${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: CHART_AXIS }} tickLine={false} axisLine={false} width={48} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v, name) => [fmtDollar(Math.round(v)), name]} labelFormatter={l => `Date: ${l}`} />
              <Legend wrapperStyle={{ fontSize: 12, color: '#8a9099' }} />
              <Area type="monotone" dataKey="Strategy"  stroke={CHART_STRATEGY} strokeWidth={2.2} fill="url(#stratGrad)" dot={false} />
              <Area type="monotone" dataKey="S&P 500"   stroke={CHART_BENCH}    strokeWidth={1.5} fill="url(#benchGrad)" dot={false} strokeDasharray="4 2" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </section>

      {phase === 'results' && (
        <section className="deploy-section">
          <p className="deploy-prompt">Happy with the strategy? Deploy it to your live portfolio, or keep refining via the chat.</p>
          <div className="deploy-actions">
            <button className="btn btn--primary" onClick={deploy}><MdRocketLaunch size={18} /> Deploy Strategy</button>
            <button className="btn btn--ghost"   onClick={resetConversation}>Reset Conversation</button>
          </div>
        </section>
      )}

      {phase === 'deployed' && (
        <section className="deploy-section deploy-section--done">
          <MdCheckCircle size={28} color="#00ff88" />
          <p className="deploy-success">Your strategy has been queued for review by our portfolio management team — switch to the Home tab to see it once it goes live.</p>
        </section>
      )}

    </div>
  );
}
